import { useState, useCallback, useMemo, useEffect } from "react";
import SmtpAccountService from "../services/SmtpAccounts";
import EmailProviderService from "../services/EmailProviders";
import CustomEmailDomainsService from "../services/CustomEmailDomains";
import {
  getResolvedProvider,
  getProviderChecks,
  shouldShowCampaignDefault,
} from "../helpers/emailHelper";
import {
  DEFAULT_PROVIDER_OPTION,
  EMAIL_SERVICES,
} from "../constants/Constants";

/**
 * Custom hook for managing email provider state and operations
 * @param {Object} options
 * @param {number} options.emailService - Email service constant
 * @param {boolean} options.onlySmtp - Whether to only show SMTP options
 * @param {Object} options.campaign - Campaign object (optional)
 * @returns {Object} Provider state and handlers
 */
export const useEmailProvider = ({
  emailService,
  onlySmtp = false,
  campaign = null,
}) => {
  const [providerState, setProviderState] = useState("");
  const [providerOptions, setProviderOptions] = useState([]);
  const [smtpOptions, setSmtpOptions] = useState([]);
  const [smtpAccount, setSmtpAccount] = useState({});
  const [smtp_config_id, setSmtpConfigId] = useState(null);
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [outlookEmailAliases, setOutlookEmailAliases] = useState([]);
  const [outlookAliasSelected, setOutlookAliasSelected] = useState(null);
  const [mailgunDomainOptions, setMailgunDomainOptions] = useState([]);
  const [mailgunDomainSelected, setMailgunDomainSelected] = useState(null);
  const [checkFromField, setCheckFromField] = useState(false);

  const provider = useMemo(
    () => getResolvedProvider(providerState, emailService, campaign),
    [providerState, emailService, campaign]
  );

  const providerChecks = useMemo(() => getProviderChecks(provider), [provider]);

  const fetchOutlookAliases = useCallback(async () => {
    try {
      const outlookEmailAliases =
        await EmailProviderService.getEmailAliasesForOutlook();
      setOutlookEmailAliases(outlookEmailAliases);
    } catch (error) {
      console.error("Error fetching Outlook aliases:", error);
      setOutlookEmailAliases([]);
    }
  }, []);

  const fetchMailgunDomains = useCallback(async () => {
    try {
      const domains = await CustomEmailDomainsService.getVerifiedDomains();
      const domainOptions = domains.map(domain => ({
        key: domain.id,
        value: domain.name,
        text: domain.name,
      }));
      const defaultDomain = domains.find(domain => domain.is_default)?.name;
      setMailgunDomainOptions(domainOptions);
      setMailgunDomainSelected(defaultDomain);
    } catch (error) {
      console.error("Error fetching Mailgun domains:", error);
      setMailgunDomainOptions([]);
    }
  }, []);

  const fetchProviders = useCallback(
    async (campaignData = null) => {
      try {
        const accounts = await SmtpAccountService.getSmtpAccounts();
        setSmtpAccounts(accounts);
        let providerOptions = await EmailProviderService.getEmailProviders(
          accounts.length > 0,
          false,
          onlySmtp
        );
        const campaignSelected = campaignData || campaign;
        if (
          shouldShowCampaignDefault(onlySmtp, emailService, campaignSelected)
        ) {
          providerOptions = [DEFAULT_PROVIDER_OPTION, ...providerOptions];
        }
        setProviderOptions(providerOptions);
      } catch (error) {
        console.error("Error fetching providers:", error);
        setProviderOptions([]);
      }
    },
    [onlySmtp, emailService, campaign]
  );

  const fetchSmtpOptions = useCallback(async () => {
    try {
      const smtpOptions = smtpAccounts.map(({ id, return_addr }) => ({
        key: id,
        text: return_addr,
        value: id,
      }));
      let smtpAccount = smtpAccounts.find(({ is_default }) => is_default);
      if (smtpAccounts.length > 0 && !smtpAccount) {
        smtpAccount = smtpAccounts[0];
      }
      setSmtpOptions(smtpOptions);
      setSmtpAccount(smtpAccount);
    } catch (error) {
      console.error("Error fetching SMTP options:", error);
      setSmtpOptions([]);
    }
  }, [smtpAccounts]);

  const handleProviderChange = useCallback(
    (e, { value }) => {
      setProviderState(value);
      const provider = getResolvedProvider(value, emailService, campaign);
      const { isSMTP, isOutlook } = getProviderChecks(provider);

      // Only check from field for non-SMTP providers
      setCheckFromField(provider !== EMAIL_SERVICES.SMTP);

      switch (provider) {
        case EMAIL_SERVICES.SMTP:
          fetchSmtpOptions();
          break;
        case EMAIL_SERVICES.OUTLOOK:
          fetchOutlookAliases();
          break;
        case EMAIL_SERVICES.MAILGUN:
          fetchMailgunDomains();
          break;
      }
    },
    [
      emailService,
      campaign,
      fetchSmtpOptions,
      fetchOutlookAliases,
      fetchMailgunDomains,
    ]
  );

  const handleFromChange = useCallback(
    (event, { value }) => {
      const smtpAccount = smtpOptions
        .filter(opt => opt.value === value)
        .map(({ value, text }) => ({
          id: value,
          return_addr: text,
        }))[0];
      setSmtpAccount(smtpAccount);
      setSmtpConfigId(value);
    },
    [smtpOptions]
  );

  const handleOutlookAliasChange = useCallback((event, data) => {
    setOutlookAliasSelected(data.value);
  }, []);

  const handleMailgunDomainChange = useCallback((event, data) => {
    setMailgunDomainSelected(data.value);
  }, []);

  const resetProvider = useCallback(() => {
    setProviderState("");
    setSmtpConfigId(null);
    setSmtpAccount({});
    setOutlookAliasSelected(null);
    setMailgunDomainSelected(null);
    setCheckFromField(false);
  }, []);

  // Auto-select SMTP when onlySmtp is true and accounts are available
  useEffect(() => {
    if (onlySmtp && smtpAccounts.length > 0 && !providerState) {
      const defaultAccount = smtpAccounts.find(({ is_default }) => is_default);
      const smtpAccount = defaultAccount || smtpAccounts[0];

      if (smtpAccount) {
        setProviderState(EMAIL_SERVICES.SMTP);
        setSmtpConfigId(smtpAccount.id);
        fetchSmtpOptions();
      }
    }
  }, [onlySmtp, smtpAccounts, providerState, fetchSmtpOptions]);

  return {
    // State
    providerState,
    providerOptions,
    smtpOptions,
    smtpAccount,
    smtp_config_id,
    smtpAccounts,
    outlookEmailAliases,
    outlookAliasSelected,
    mailgunDomainOptions,
    mailgunDomainSelected,
    checkFromField,
    provider,
    providerChecks,

    // Handlers
    handleProviderChange,
    handleFromChange,
    handleOutlookAliasChange,
    handleMailgunDomainChange,
    fetchProviders,
    fetchSmtpOptions,
    fetchOutlookAliases,
    fetchMailgunDomains,
    resetProvider,

    // Setters (for advanced use cases)
    setProviderState,
    setSmtpConfigId,
  };
};
